<script lang="ts">
  import type { AgencyGroupNode } from '@gat-ship/types'
  import {
    IconArchive,
    IconChevronDown,
    IconDotsHorizontal,
    IconEdit,
    IconGroup,
    IconMoveVertical,
    IconAgencyStructure,
    IconPlus
  } from '@gat-ship/icons'
  import { computed, ref } from 'vue'
  import { inject } from 'vue'
  import AgencyListItem from './AgencyListItem.vue'
  import { agencyStructureModalsKey } from '../providers/agency-structure-modals'

  export type ListItemAgencyGroupProps = {
    isCollapsible?: boolean
    node: AgencyGroupNode
  }

  const highlightId = ref<string | undefined>(undefined)
  let leaveTimeoutId: ReturnType<typeof setTimeout> | null = null
</script>

<script setup lang="ts">
  import { onMounted, onUnmounted } from 'vue'
  import { testIds } from '@gat-ship/types'
  import { useAgencyStructureSelectors } from '../../composables/useAgencyStructureSelectors'
  import { useUser } from '@gat-ship/user'
  import { TooltipHint } from '@gat-ship/vue-components'
  import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem
  } from '@gat-ship/vue-components'

  const user = useUser()

  defineOptions({ name: 'AgencyGroupListItem' })

  const props = withDefaults(defineProps<ListItemAgencyGroupProps>(), {
    isCollapsible: true
  })

  const isOpen = defineModel<boolean>('open', { default: true })

  function toggleIsOpen() {
    if (props.isCollapsible) {
      isOpen.value = !isOpen.value
    }
  }

  const { groupChildren } = useAgencyStructureSelectors()
  const { children, agencies, agencyGroups } = groupChildren(
    () => props.node.id
  )

  const agencyStructureModals = inject(agencyStructureModalsKey)

  const rootEl = ref<HTMLElement | null>(null)

  const title = computed(() => {
    return props.node.name
  })

  const description = computed(() => {
    return undefined
  })

  function findDeepestGroup(element: Node | null): HTMLElement | null {
    if (!element || !(element instanceof HTMLElement)) {
      return null
    }

    // Walk up the tree and return the FIRST group we encounter (the deepest/most nested)
    let current: HTMLElement | null = element

    while (current) {
      if (current.dataset.agencyGroupId) {
        return current // Return immediately - this is the deepest group
      }
      current = current.parentElement
    }

    return null
  }

  function onMouseMove(event: MouseEvent) {
    if (leaveTimeoutId !== null) {
      clearTimeout(leaveTimeoutId)
      leaveTimeoutId = null
    }

    const target = event.target as Node | null
    const deepestGroup = findDeepestGroup(target)

    if (deepestGroup) {
      highlightId.value = deepestGroup.dataset.agencyGroupId!
    }
  }

  function onMouseLeave(event: MouseEvent) {
    const related = event.relatedTarget as Node | null

    // If moving to a child element, don't clear
    if (related && rootEl.value?.contains(related)) {
      return
    }

    // Small delay to check if we're actually leaving or moving to another group
    leaveTimeoutId = setTimeout(() => {
      // Use the last known mouse position to check what's under the cursor
      const elementUnderCursor = document.elementFromPoint(
        event.clientX,
        event.clientY
      )
      const deepestGroup = findDeepestGroup(elementUnderCursor)

      if (deepestGroup) {
        highlightId.value = deepestGroup.dataset.agencyGroupId!
      } else {
        highlightId.value = undefined
      }
      leaveTimeoutId = null
    }, 50)
  }

  onMounted(() => {
    if (rootEl.value) {
      rootEl.value.addEventListener('mousemove', onMouseMove, { passive: true })
    }
  })

  onUnmounted(() => {
    if (rootEl.value) {
      rootEl.value.removeEventListener('mousemove', onMouseMove)
    }
    if (leaveTimeoutId !== null) {
      clearTimeout(leaveTimeoutId)
    }
  })
</script>

<template>
  <div
    ref="rootEl"
    :data-agency-group-id="node.id"
    :data-testid="testIds.agency.list(node.id)"
    class="group relative flex flex-col"
    :class="{
      'before:absolute before:bottom-0 before:left-3 before:top-12 before:w-px before:bg-neutral-200 before:bg-neutral-400 before:content-[\'\'] dark:before:bg-neutral-600 dark:before:bg-neutral-700':
        highlightId === node.id && isOpen && children.length > 0
    }"
    @mouseleave="onMouseLeave"
  >
    <div class="flex select-none flex-col">
      <div
        class="relative flex min-h-16 grow items-center gap-4 py-4"
        :class="{
          'cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-100':
            isCollapsible
        }"
        @click="toggleIsOpen()"
      >
        <div
          v-if="isCollapsible"
          class="flex size-6 items-center justify-center rounded-full bg-neutral-100 transition-all duration-150 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700"
        >
          <icon-chevron-down class="size-4" :class="{ 'rotate-180': isOpen }" />
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex items-end gap-1 self-center">
            <div
              class="text-base font-semibold leading-none tracking-wide"
              :data-testid="testIds.agency.listName(node.id)"
            >
              {{ title }}
            </div>

            <TooltipHint v-if="description" :text="description" />
          </div>
        </div>
        <div class="ml-auto flex items-center gap-2">
          <DropdownMenu v-if="user.isTenantAdmin.value">
            <DropdownMenuTrigger as-child>
              <div
                class="flex size-8 cursor-pointer items-center justify-center rounded-full bg-neutral-100 transition-all duration-150 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700"
                :data-testid="testIds.agency.addActions(node.id)"
                @click.stop
              >
                <IconPlus class="size-4" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                v-if="user.isTenantAdmin.value"
                :data-testid="testIds.agency.action.addAgency"
                @click="
                  agencyStructureModals?.showAddAgencyModal({
                    parentGroupId: node.id
                  })
                "
              >
                <IconAgencyStructure class="size-4" />
                Add Agency
              </DropdownMenuItem>
              <DropdownMenuItem
                v-if="user.isTenantAdmin.value"
                :data-testid="testIds.agency.action.addGroup"
                @click="
                  agencyStructureModals?.showAddAgencyGroupModal({
                    parentGroupId: node.id
                  })
                "
              >
                <IconGroup class="size-4" />
                Add Group
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <div
                class="flex size-8 cursor-pointer items-center justify-center rounded-full bg-neutral-100 transition-all duration-150 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700"
                :data-testid="testIds.agency.actions(node.id)"
                @click.stop
              >
                <icon-dots-horizontal class="size-4" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                v-if="node.canChangeName"
                :data-testid="testIds.agency.action.changeName"
                @click="
                  agencyStructureModals?.showChangeAgencyGroupNameModal({
                    agencyGroup: node
                  })
                "
              >
                <IconEdit class="size-4" />
                Change Group Name
              </DropdownMenuItem>
              <DropdownMenuItem
                v-if="node.canMove"
                :data-testid="testIds.agency.action.moveAgencyNode"
                @click="
                  agencyStructureModals?.showMoveAgencyGroupModal({
                    agencyGroup: node
                  })
                "
              >
                <IconMoveVertical class="size-4" />
                Move Group
              </DropdownMenuItem>
              <DropdownMenuItem
                v-if="user.isTenantAdmin.value && node.canRemove"
                :data-testid="testIds.agency.action.removeGroup"
                @click="
                  agencyStructureModals?.showRemoveAgencyGroupModal({
                    agencyGroup: node,
                    canDelete: node.canRemove
                  })
                "
              >
                <IconArchive class="size-4" />
                Remove Group
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
    <div
      class="-mb-[2px] ml-8 flex flex-col divide-y divide-neutral-200 overflow-hidden rounded-md dark:divide-neutral-700"
      :class="{
        hidden: !isOpen
      }"
    >
      <AgencyListItem
        v-for="agency in agencies"
        :key="agency.id"
        :agency-node="agency"
      />
    </div>
    <div class="ml-8 flex flex-col" :class="{ hidden: !isOpen }">
      <AgencyGroupListItem
        v-for="agencyGroup in agencyGroups"
        :key="agencyGroup.id"
        :node="agencyGroup"
      />
    </div>
  </div>
</template>
